import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { useSwipeTabNavigation } from "./hooks/useSwipeTabNavigation";
import { ScrollRestoration } from "./components/ScrollRestoration";
import { Navbar } from "./components/Navbar";
import { SessionTimeoutHandler } from "./components/SessionTimeoutHandler";
import { AuthProvider } from "./contexts/AuthContext";
const nightspyLogo = "/nightspy-owl-loader.png";
import { EventManageModalMount } from "./components/EventManageModal";
import { SignInDialogMount } from "./components/SignInDialog";
import { TermsReacceptanceGate } from "./components/TermsReacceptanceGate";
import { CreateEventModalMount, preloadCreateEventModal } from "./components/CreateEventModal";
import { preloadEventManageModal } from "./components/EventManageModal";
import { ViewModeRouteSync } from "./components/ViewModeRouteSync";
import { useEffect } from "react";
import { installErrorReporter } from "./lib/error-reporter";

installErrorReporter();

// Lazy load with stale-chunk recovery: when a deploy invalidates old JS chunks,
// backgrounded tabs throw "Failed to fetch dynamically imported module".
// We retry once, then force a hard reload so the user gets the new bundle.
const lazyWithRetry = <T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
) =>
  lazy(async () => {
    const RELOAD_KEY = "nightspy_chunk_reloaded_at";
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || err || "");
      const isChunkError =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module") ||
        msg.includes("Loading chunk");
      if (!isChunkError) throw err;
      // The old chunk URL no longer exists after a deploy, so retrying the same
      // import can never succeed. Reload the page with a cache-busting param to
      // pull the fresh index.html + chunk manifest.
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > 20_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        const url = new URL(window.location.href);
        url.searchParams.set("v", String(Date.now()));
        window.location.replace(url.toString());
        // Keep the tree suspended while the reload happens.
        return await new Promise<T>(() => {});
      }
      throw err;
    }
  });


const Home = lazyWithRetry(() => import("./pages/Home"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const CompleteRegistration = lazyWithRetry(() => import("./pages/CompleteRegistration"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Events = lazyWithRetry(() => import("./pages/Events"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const CreateEvent = lazyWithRetry(() => import("./pages/CreateEvent"));
const EditEvent = lazyWithRetry(() => import("./pages/EditEvent"));
const EventDetail = lazyWithRetry(() => import("./pages/EventDetail"));
const ManageEvent = lazyWithRetry(() => import("./pages/ManageEvent"));
const EventCheckIn = lazyWithRetry(() => import("./pages/EventCheckIn"));
const MyTickets = lazyWithRetry(() => import("./pages/MyTickets"));
const ScanQR = lazyWithRetry(() => import("./pages/ScanQR"));
const ScanTickets = lazyWithRetry(() => import("./pages/ScanTickets"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Backlog = lazyWithRetry(() => import("./pages/Backlog"));
const PromoterScan = lazyWithRetry(() => import("./pages/PromoterScan"));
const PromoterManage = lazyWithRetry(() => import("./pages/PromoterManage"));


// Lazy-load non-critical chrome (banners, welcome modals, intro tours) to keep
// the initial JS bundle small. These render nothing for most first-time visitors.
const EmailVerificationBanner = lazy(() => import("./components/EmailVerificationBanner").then(m => ({ default: m.EmailVerificationBanner })));
const MissingNameBanner = lazy(() => import("./components/MissingNameBanner").then(m => ({ default: m.MissingNameBanner })));
const StripeTaxWarningBanner = lazy(() => import("./components/StripeTaxWarningBanner").then(m => ({ default: m.StripeTaxWarningBanner })));
const PromoterWelcomeModal = lazy(() => import("./components/PromoterWelcomeModal").then(m => ({ default: m.PromoterWelcomeModal })));
const PromoterIntroTour = lazy(() => import("./components/PromoterIntroTour").then(m => ({ default: m.PromoterIntroTour })));
const OrganizerWelcomeModal = lazy(() => import("./components/OrganizerWelcomeModal").then(m => ({ default: m.OrganizerWelcomeModal })));
const OrganizerIntroTour = lazy(() => import("./components/OrganizerIntroTour").then(m => ({ default: m.OrganizerIntroTour })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - reduces refetching
      gcTime: 1000 * 60 * 5, // 5 minutes cache
      retry: 1,
      refetchOnWindowFocus: false, // Prevents aggressive refetching
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="h-[calc(100dvh-8rem)] md:h-[calc(100dvh-4rem)] bg-background flex items-center justify-center">
    <div className="animate-pulse">
      <img src={nightspyLogo} alt="Loading" className="h-40 w-40 invert dark:invert-0" />
    </div>
  </div>
);

// Conditionally render chrome based on ?embed=1 query param
const isEmbedded = () => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("embed") === "1";
};

// Swipe left/right to move between the bottom-nav tabs on mobile.
// Must live inside <BrowserRouter>, so it renders as a component there.
const SwipeTabNavigation = () => {
  useSwipeTabNavigation();
  return null;
};

const AppShell = () => {
  const embedded = isEmbedded();



  // Idle-prefetch the heavy modal chunks so click-to-open is instant.
  // requestIdleCallback waits until the main thread is free; falls back to setTimeout.
  useEffect(() => {
    if (embedded) return;
    const ric: (cb: () => void) => number =
      (window as any).requestIdleCallback?.bind(window) ??
      ((cb: () => void) => window.setTimeout(cb, 1500));
    const id = ric(() => {
      preloadEventManageModal({ edit: true });
      preloadCreateEventModal();
    });
    return () => {
      const cancel = (window as any).cancelIdleCallback?.bind(window) ?? window.clearTimeout;
      try { cancel(id); } catch {}
    };
  }, [embedded]);

  return (
      <BrowserRouter>
        <ScrollRestoration />
        <ViewModeRouteSync />
        {!embedded && <SwipeTabNavigation />}
      {!embedded && <SessionTimeoutHandler />}
      {!embedded && <Navbar />}
      {!embedded && (
        <Suspense fallback={null}>
          <EmailVerificationBanner />
          <MissingNameBanner />
          <StripeTaxWarningBanner />
        </Suspense>
      )}
      <div className={embedded ? "min-h-screen" : "pb-16 md:pb-0 min-h-screen"}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/complete-registration" element={<CompleteRegistration />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/events" element={<Events />} />
            <Route path="/event/:slug" element={<EventDetail />} />
            <Route path="/event/:slug/edit" element={<EditEvent />} />
            <Route path="/manage" element={<Dashboard />} />
            <Route path="/create-event" element={<CreateEvent />} />
            <Route path="/manage/:id" element={<ManageEvent />} />
            <Route path="/check-in/:id" element={<EventCheckIn />} />
            <Route path="/my-tickets" element={<MyTickets />} />
            <Route path="/scan" element={<ScanQR />} />
            <Route path="/scan-tickets" element={<ScanTickets />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/backlog" element={<Backlog />} />
            <Route path="/promoter-scan" element={<PromoterScan />} />
            <Route path="/promoter/manage" element={<PromoterManage />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {!embedded && <MobileBottomNav />}
      {!embedded && <EventManageModalMount />}
      {!embedded && <SignInDialogMount />}
      {!embedded && <CreateEventModalMount />}
      {!embedded && <TermsReacceptanceGate />}
      {!embedded && (
        <Suspense fallback={null}>
          <PromoterWelcomeModal />
          <PromoterIntroTour />
          <OrganizerWelcomeModal />
          <OrganizerIntroTour />
        </Suspense>
      )}
    </BrowserRouter>
  );
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <TooltipProvider>
          <Sonner />
        <AppShell />
      </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
